import { useState } from 'react';
import { useSelector } from "react-redux";
import {
    Paper, Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Grid, TextField, Autocomplete, Collapse, Chip, Stack, IconButton
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { hotelCostCalculation, calculateSingleHotelCost, calculateSingleHotelCostWithBreakdown, determineHotelSeason } from '../createInquiryCalculation';

const HotelSelectionCard = ({
    tripDetails,
    allHotels,
    hotelSelections,
    stayInfo,
    handleHotelReset,
    selectedDay,
    setSelectedDay,
    handleHotelChange,
    handleHotelBatchChange,
    handleAddHotelRoom,
    handleAddHotelExtra,
    handleRemoveHotelRoom,
    onViewHotel
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const { user } = useSelector(state => state.tokens);
    if (!tripDetails.duration || tripDetails.duration <= 0 || allHotels.length === 0) return null;

    const selectedHotelsCount = Object.keys(hotelSelections).filter(dayIndex => {
        const daySelections = Array.isArray(hotelSelections[dayIndex]) 
            ? hotelSelections[dayIndex] 
            : (hotelSelections[dayIndex] ? [hotelSelections[dayIndex]] : []);
        return daySelections.some(h => h && h.hotelId);
    }).length;

    const totalHotelCost = hotelCostCalculation(hotelSelections, allHotels, null, tripDetails, stayInfo);

    const uniqueLocations = Array.from(new Set(allHotels.filter(h => h.isActive !== false).flatMap(h => [h.location, h.sub_destination]).filter(Boolean)));

    // Helpers for Pricing Status
    const hasHotelPrice = (hotelId, dayIndex) => {
        const hotel = allHotels.find(h => h._id?.toString() === hotelId?.toString());
        if (!hotel) return false;
        const autoSeason = determineHotelSeason(hotel, tripDetails.travel_date, dayIndex);
        return hotel.category?.some(cat => {
            const pricing = cat[autoSeason];
            if (!pricing) return false;
            return ['cp_plan', 'map_plan', 'ap_plan'].some(plan => pricing[plan] > 0);
        });
    };

    const hasRoomPrice = (hotelId, roomCat, dayIndex) => {
        const hotel = allHotels.find(h => h._id?.toString() === hotelId?.toString());
        const cat = hotel?.category?.find(c => c.room_cat === roomCat);
        if (!cat) return false;
        const autoSeason = determineHotelSeason(hotel, tripDetails.travel_date, dayIndex);
        const pricing = cat[autoSeason];
        if (!pricing) return false;
        return ['cp_plan', 'map_plan', 'ap_plan'].some(plan => pricing[plan] > 0);
    };

    const hasMealPlanPrice = (hotelId, roomCat, mealPlan, dayIndex) => {
        if (!mealPlan) return false;
        const hotel = allHotels.find(h => h._id?.toString() === hotelId?.toString());
        const cat = hotel?.category?.find(c => c.room_cat === roomCat);
        const autoSeason = determineHotelSeason(hotel, tripDetails.travel_date, dayIndex);
        const pricing = cat?.[autoSeason];
        return pricing?.[mealPlan] > 0;
    };

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isExpanded ? 2 : 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        cursor: 'pointer',
                        flex: 1,
                        transition: 'opacity 0.2s',
                        '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Select Hotels Day-Wise
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selectedHotelsCount > 0 && (
                            <Chip
                                label={`Days with Hotels: ${selectedHotelsCount}/${tripDetails.duration}`}
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                        )}
                        {(user?.permission === 'Admin' && totalHotelCost > 0) && (
                            <Chip
                                label={`Total Hotel Cost: ₹${totalHotelCost.toLocaleString()}`}
                                size="small"
                                color="info"
                                variant="filled"
                            />
                        )}
                    </Stack>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                    <Button
                        size="small"
                        color="warning"
                        onClick={handleHotelReset}
                    >
                        Reset
                    </Button>
                </Box>
            </Box>
            <Collapse in={isExpanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {Array.from({ length: parseInt(tripDetails.duration) }, (_, dayIndex) => {
                        let daySelections = Array.isArray(hotelSelections[dayIndex])
                            ? hotelSelections[dayIndex]
                            : (hotelSelections[dayIndex] ? [hotelSelections[dayIndex]] : [{}]);

                        if (daySelections.length === 0) daySelections = [{}];

                        // ── Per-day pax split calculation ──────────────────────
                        const totalAdults = parseInt(tripDetails?.pax) || 0;
                        const totalKids = parseInt(tripDetails?.kids_above_5) || 0;
                        const totalDayRooms = daySelections.reduce((sum, s) => sum + (parseInt(s?.roomsCount) || 1), 0);
                        const totalDayCapacity = totalDayRooms * 2;
                        const totalExtraAdults = Math.max(0, totalAdults - totalDayCapacity);
                        const hasAnyManualExtra = daySelections.some(s => s?.extraCount !== undefined && s?.extraCount !== null && s?.extraCount !== '');
                        const totalDayExtra = hasAnyManualExtra
                            ? daySelections.reduce((sum, s) => {
                                if (s?.extraCount !== undefined && s?.extraCount !== null && s?.extraCount !== '') {
                                    return sum + Math.max(0, parseInt(s.extraCount) || 0);
                                }
                                const rRooms = parseInt(s?.roomsCount) || 1;
                                const rAdults = totalDayRooms > 0 ? Math.round(totalAdults * rRooms / totalDayRooms) : 0;
                                return sum + Math.max(0, rAdults - rRooms * 2);
                            }, 0)
                            : totalExtraAdults;
                        const multiRow = daySelections.length > 1;

                        // Adults proportionally assigned per room row
                        const getRowAdults = (rowRooms) =>
                            totalDayRooms > 0 ? Math.round(totalAdults * rowRooms / totalDayRooms) : 0;

                        return (
                            <Box
                                key={dayIndex}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    backgroundColor: 'transparent',
                                }}
                            >
                                {/* ── Day header row: label + pax summary + add button ── */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', minWidth: 60 }}
                                        onClick={() => setSelectedDay(dayIndex)}
                                    >
                                        Day {dayIndex + 1}
                                    </Typography>

                                    {/* Pax summary for this day */}
                                    <Box sx={{
                                        flex: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
                                        px: 1.5, py: 0.8, borderRadius: 1.5,
                                        bgcolor: '#f0f9ff', border: '1px solid #bae6fd',
                                    }}>
                                        <Chip size="small" label={`${totalAdults} Adults`} sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: 11 }} />
                                        {totalKids > 0 && <Chip size="small" label={`${totalKids} Kids (5+) → CNB`} sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontWeight: 600, fontSize: 11 }} />}
                                        <Typography sx={{ fontSize: 11, color: '#475569' }}>
                                            {totalDayRooms} room{totalDayRooms !== 1 ? 's' : ''} · capacity {totalDayCapacity}
                                            {multiRow && <span style={{ color: '#0369a1', fontWeight: 600 }}> · {daySelections.length} hotel rows</span>}
                                        </Typography>
                                        {totalDayExtra > 0
                                            ? <Chip size="small" label={`${hasAnyManualExtra ? '✓ ' : '⚠ '}${totalDayExtra} extra mattress${hasAnyManualExtra ? ' (manual)' : ''}`} sx={{ bgcolor: hasAnyManualExtra ? '#eff6ff' : '#fef2f2', color: hasAnyManualExtra ? '#1d4ed8' : '#b91c1c', fontWeight: 700, fontSize: 11, border: hasAnyManualExtra ? '1px solid #bfdbfe' : '1px solid #fecaca' }} />
                                            : <Chip size="small" label={hasAnyManualExtra ? "✓ 0 extra mattress (manual)" : "✓ All adults fit"} sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600, fontSize: 11 }} />
                                        }
                                        {multiRow && (
                                            <Typography sx={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>
                                                Pax split proportionally across rooms
                                            </Typography>
                                        )}
                                    </Box>

                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleAddHotelRoom(dayIndex)}
                                            sx={{ textTransform: 'none', px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                        >
                                            + Room
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleAddHotelExtra && handleAddHotelExtra(dayIndex)}
                                            sx={{ textTransform: 'none', px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                                        >
                                            + Extra
                                        </Button>
                                    </Stack>
                                </Box>

                                {/* Hotel Selection Fields */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {daySelections.map((selection, roomIndex) => {
                                        const rowRooms = parseInt(selection?.roomsCount) || 1;
                                        // Proportional adults for this row
                                        const rowAdults = getRowAdults(rowRooms);
                                        const getRowKids = (rowRooms) => totalDayRooms > 0 ? Math.round(totalKids * rowRooms / totalDayRooms) : 0;
                                        const rowKids = getRowKids(rowRooms);
                                        const rowExtraAdults = Math.max(0, rowAdults - rowRooms * 2);
                                        const specificStayInfo = { ...stayInfo, rooms: rowRooms };
                                        // Override tripDetails.pax and kids with proportional count for cost calc
                                        const rowTripDetails = { ...tripDetails, pax: rowAdults, kids_above_5: rowKids };
                                        const dayCost = calculateSingleHotelCost(selection, allHotels, dayIndex, rowTripDetails, specificStayInfo);

                                        
                                        return (
                                            <Box key={roomIndex} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <Grid container spacing={2} sx={{ flex: 1 }}>
                                                    {/* Step 1: Location */}
                                                    <Grid item xs={12} sm={6} md={1.5}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Location"
                                                            value={selection?.location || ''}
                                                            onChange={(e) => handleHotelChange(dayIndex, roomIndex, 'location', e.target.value)}
                                                        />
                                                    </Grid>

                                                    {/* Step 1.5: Filter Location */}
                                                    <Grid item xs={12} sm={6} md={1.4}>
                                                        <Autocomplete
                                                            fullWidth
                                                            size="small"
                                                            freeSolo
                                                            options={uniqueLocations}
                                                            value={selection?.filterLocation || ''}
                                                            onInputChange={(e, newValue) => {
                                                                if (newValue && !selection?.hotelId) {
                                                                    // Search across ALL days for a matching filled row
                                                                    let template = null;
                                                                    const allDayKeys = Object.keys(hotelSelections);
                                                                    for (const dk of allDayKeys) {
                                                                        const rows = Array.isArray(hotelSelections[dk])
                                                                            ? hotelSelections[dk]
                                                                            : (hotelSelections[dk] ? [hotelSelections[dk]] : []);
                                                                        const match = rows.find(s =>
                                                                            s?.hotelId &&
                                                                            (s?.filterLocation || '').toLowerCase() === newValue.toLowerCase()
                                                                        );
                                                                        if (match) { template = match; break; }
                                                                    }
                                                                    if (template) {
                                                                        handleHotelBatchChange(dayIndex, roomIndex, {
                                                                            filterLocation: newValue,
                                                                            hotelId: template.hotelId,
                                                                            roomType: template.roomType,
                                                                            mealPlan: template.mealPlan,
                                                                        });
                                                                        return;
                                                                    }
                                                                }
                                                                handleHotelChange(dayIndex, roomIndex, 'filterLocation', newValue);
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Filter By"
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        sx: selection?.hotelId ? {} : {
                                                                            '& fieldset': { borderColor: '#94a3b8', borderStyle: 'dashed' },
                                                                        },
                                                                    }}
                                                                    helperText={!selection?.hotelId && daySelections.length > 1 ? 'Match location to auto-fill' : ''}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>

                                                    {/* Step 2: Hotel Selection */}
                                                    <Grid item xs={12} sm={6} md={2.3}>
                                                        <Autocomplete
                                                            fullWidth
                                                            size="small"
                                                            options={allHotels.filter(h => {
                                                                // Inactive hotels must NOT be visible in create inquiry form
                                                                if (h.isActive === false && h._id?.toString() !== selection?.hotelId?.toString()) {
                                                                    return false;
                                                                }
                                                                // Always keep the currently selected hotel in options
                                                                // so MUI doesn't clear value when filterLocation changes
                                                                if (h._id && h._id?.toString() === selection?.hotelId?.toString()) return true;
                                                                const currentLoc = selection?.filterLocation?.toLowerCase() || '';
                                                                if (!currentLoc) return true;
                                                                const hLoc = (h.location || '').toLowerCase();
                                                                const hSub = (h.sub_destination || '').toLowerCase();
                                                                return (hLoc && currentLoc.includes(hLoc)) ||
                                                                       (hLoc && hLoc.includes(currentLoc)) ||
                                                                       (hSub && currentLoc.includes(hSub)) ||
                                                                       (hSub && hSub.includes(currentLoc));
                                                            })}
                                                            getOptionLabel={(option) => `${option.hotel_name} (${option.sub_destination || option.location})`}
                                                            value={allHotels.find(h => h._id?.toString() === selection?.hotelId?.toString()) || null}
                                                            onChange={(event, newValue) => {
                                                                handleHotelChange(dayIndex, roomIndex, 'hotelId', newValue?._id || '');
                                                            }}
                                                            renderOption={(props, option) => {
                                                                const { key, ...optionProps } = props;
                                                                const priced = hasHotelPrice(option._id, dayIndex);
                                                                return (
                                                                    <Box
                                                                        key={key}
                                                                        component="li"
                                                                        {...optionProps}
                                                                        sx={{
                                                                            backgroundColor: priced ? '#e8f5e9 !important' : '#ffebee !important',
                                                                            '&:hover': {
                                                                                backgroundColor: priced ? '#c8e6c9 !important' : '#ffcdd2 !important',
                                                                            }
                                                                        }}
                                                                    >
                                                                        {option.hotel_name} ({option.sub_destination || option.location})
                                                                    </Box>
                                                                );
                                                            }}
                                                            renderInput={(params) => <TextField {...params} label="Select Hotel" size="small" />}
                                                        />
                                                    </Grid>

                                                    {/* Step 3: Room Type */}
                                                    <Grid item xs={12} sm={6} md={1.8}>
                                                        <FormControl fullWidth size="small" disabled={!selection?.hotelId}>
                                                            <InputLabel>Room Type</InputLabel>
                                                            <Select
                                                                value={selection?.roomType || ''}
                                                                label="Room Type"
                                                                onChange={(e) => {
                                                                    handleHotelChange(dayIndex, roomIndex, 'roomType', e.target.value);
                                                                }}
                                                            >
                                                                {allHotels.find(h => h._id?.toString() === selection?.hotelId?.toString())?.category?.map((cat, index) => {
                                                                    const priced = hasRoomPrice(selection?.hotelId, cat.room_cat, dayIndex);
                                                                    return (
                                                                        <MenuItem
                                                                            key={index}
                                                                            value={cat.room_cat}
                                                                            sx={{
                                                                                backgroundColor: priced ? '#e8f5e9' : '#ffebee',
                                                                                '&:hover': {
                                                                                    backgroundColor: priced ? '#c8e6c9' : '#ffcdd2',
                                                                                },
                                                                                '&.Mui-selected': {
                                                                                    backgroundColor: priced ? '#a5d6a7' : '#ef9a9a',
                                                                                    '&:hover': {
                                                                                        backgroundColor: priced ? '#81c784' : '#e57373',
                                                                                    }
                                                                                }
                                                                            }}
                                                                        >
                                                                            {cat.room_cat}
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Step 4: Meal Plan */}
                                                    <Grid item xs={12} sm={6} md={1.6}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Meal Plan</InputLabel>
                                                            <Select
                                                                value={selection?.mealPlan || ''}
                                                                label="Meal Plan"
                                                                onChange={(e) => {
                                                                    handleHotelChange(dayIndex, roomIndex, 'mealPlan', e.target.value);
                                                                }}
                                                            >
                                                                {[
                                                                    { value: 'cp_plan', label: 'CP' },
                                                                    { value: 'map_plan', label: 'MAP' },
                                                                    { value: 'ap_plan', label: 'AP' }
                                                                ].map((plan) => {
                                                                    const priced = hasMealPlanPrice(
                                                                        selection?.hotelId,
                                                                        selection?.roomType,
                                                                        plan.value,
                                                                        dayIndex
                                                                    );
                                                                    return (
                                                                        <MenuItem
                                                                            key={plan.value}
                                                                            value={plan.value}
                                                                            sx={{
                                                                                backgroundColor: priced ? '#e8f5e9' : '#ffebee',
                                                                                '&:hover': {
                                                                                    backgroundColor: priced ? '#c8e6c9' : '#ffcdd2',
                                                                                },
                                                                                '&.Mui-selected': {
                                                                                    backgroundColor: priced ? '#a5d6a7' : '#ef9a9a',
                                                                                    '&:hover': {
                                                                                        backgroundColor: priced ? '#81c784' : '#e57373',
                                                                                    }
                                                                                }
                                                                            }}
                                                                        >
                                                                            {plan.label}
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Step 5: Room Count */}
                                                    <Grid item xs={12} sm={6} md={1.1}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Rooms"
                                                            type="number"
                                                            value={parseInt(selection?.roomsCount) || (parseInt(stayInfo?.rooms) > 0 ? parseInt(stayInfo.rooms) : 1)}
                                                            onChange={(e) => handleHotelChange(dayIndex, roomIndex, 'roomsCount', parseInt(e.target.value) || 1)}
                                                            inputProps={{ min: 1 }}
                                                        />
                                                    </Grid>

                                                    {/* Step 5.5: Extra Mattress Count */}
                                                    <Grid item xs={12} sm={6} md={1.1}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Extra Mat"
                                                            type="number"
                                                            value={
                                                                selection?.extraCount !== undefined && selection?.extraCount !== null && selection?.extraCount !== ''
                                                                    ? selection.extraCount
                                                                    : (stayInfo?.extra_mattress !== undefined && stayInfo?.extra_mattress !== ''
                                                                        ? stayInfo.extra_mattress
                                                                        : (rowExtraAdults || 0))
                                                            }
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                handleHotelChange(
                                                                    dayIndex,
                                                                    roomIndex,
                                                                    'extraCount',
                                                                    val === '' ? '' : Math.max(0, parseInt(val) || 0)
                                                                );
                                                            }}
                                                            inputProps={{ min: 0 }}
                                                            helperText={selection?.extraCount !== undefined && selection?.extraCount !== '' ? "Manual" : "Auto"}
                                                            FormHelperTextProps={{
                                                                sx: {
                                                                    fontSize: '9px',
                                                                    m: 0,
                                                                    textAlign: 'right',
                                                                    color: selection?.extraCount !== undefined && selection?.extraCount !== '' ? 'primary.main' : 'text.secondary'
                                                                }
                                                            }}
                                                        />
                                                    </Grid>

                                                    {/* Step 6: Actions */}
                                                    <Grid item xs={12} sm={6} md={1.2} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
                                                        {selection?.hotelId && (
                                                            <Button
                                                                variant="outlined"
                                                                color="info"
                                                                size="small"
                                                                startIcon={<VisibilityIcon />}
                                                                onClick={() => {
                                                                    if (onViewHotel) onViewHotel(selection.hotelId);
                                                                }}
                                                                sx={{ textTransform: 'none', px: 1, minWidth: 'auto', fontSize: '0.75rem', py: 0.5 }}
                                                            >
                                                                View
                                                            </Button>
                                                        )}
                                                        {daySelections.length > 1 && (
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                size="small"
                                                                startIcon={<DeleteIcon />}
                                                                onClick={() => handleRemoveHotelRoom(dayIndex, roomIndex)}
                                                                sx={{ textTransform: 'none', px: 1, minWidth: 'auto', fontSize: '0.75rem', py: 0.5 }}
                                                            >
                                                                Remove
                                                            </Button>
                                                        )}
                                                    </Grid>
                                                    
                                                </Grid>
                                              </Box>

                                                {/* ── Cost breakdown strip ── */}
                                                {(() => {
                                                    if (!selection?.hotelId || !selection?.roomType || !selection?.mealPlan) {
                                                        // Show pax allocation hint even before hotel is selected (multi-row)
                                                        if (!multiRow) return null;
                                                        return (
                                                            <Box sx={{ px: 1.5, py: 0.6, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                                                                    This row: <b>{rowRooms}</b> room{rowRooms !== 1 ? 's' : ''} →
                                                                    <b> ~{rowAdults}</b> adult{rowAdults !== 1 ? 's' : ''} allocated
                                                                    {rowExtraAdults > 0 && <span style={{ color: '#b91c1c' }}> · {rowExtraAdults} extra mattress</span>}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    }
                                                    const bd = calculateSingleHotelCostWithBreakdown(selection, allHotels, dayIndex, rowTripDetails, specificStayInfo);
                                                    if (!bd || bd.total === 0) return null;
                                                    const mealLabel = { cp_plan: 'CP', map_plan: 'MAP', ap_plan: 'AP' }[selection.mealPlan] || selection.mealPlan;
                                                    const adultsInRoom = Math.min(rowAdults, rowRooms * 2);
                                                    return (
                                                        <Box sx={{
                                                            px: 1.5, py: 0.8,
                                                            bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                                                            borderRadius: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center',
                                                        }}>
                                                            {/* Pax allocation for this row */}
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#eff6ff', px: 1, py: 0.3, borderRadius: 1 }}>
                                                                <Typography sx={{ fontSize: 10, color: '#1d4ed8', fontWeight: 600 }}>
                                                                    {rowAdults} adult{rowAdults !== 1 ? 's' : ''}
                                                                    {multiRow ? ` (split from ${totalAdults})` : ''}
                                                                    {rowRooms > 1 ? ` · ${Math.ceil(adultsInRoom / rowRooms)}/room` : ''}
                                                                </Typography>
                                                            </Box>
                                                            {/* Room base cost */}
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                                                                    {bd.details.rooms}rm × ₹{bd.details.basePrice.toLocaleString()} {mealLabel}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1e293b' }}>
                                                                    = ₹{bd.baseCost.toLocaleString()}
                                                                </Typography>
                                                            </Box>
                                                            {/* Extra mattress */}
                                                            {bd.details.extraAdults > 0 && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#fef2f2', px: 1, py: 0.3, borderRadius: 1, border: '1px solid #fecaca' }}>
                                                                    <Typography sx={{ fontSize: 10, color: '#b91c1c' }}>
                                                                        {bd.details.extraAdults} extra mat × ₹{bd.details.extraPrice.toLocaleString()} = ₹{bd.extraAdultCost.toLocaleString()}{bd.details.isManualExtra && <span style={{ fontWeight: 700, marginLeft: 4, color: '#1d4ed8' }}>(manual)</span>}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {/* CNB kids */}
                                                            {bd.details.kids > 0 && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#fffbeb', px: 1, py: 0.3, borderRadius: 1, border: '1px solid #fde68a' }}>
                                                                    <Typography sx={{ fontSize: 10, color: '#92400e' }}>
                                                                        {bd.details.kids} CNB × ₹{bd.details.childPrice.toLocaleString()} = ₹{bd.childCost.toLocaleString()}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {/* Row total */}
                                                            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#eff6ff', px: 1.2, py: 0.4, borderRadius: 1, border: '1px solid #bfdbfe' }}>
                                                                <Typography sx={{ fontSize: 10, color: '#1d4ed8' }}>Night total</Typography>
                                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1d4ed8' }}>
                                                                    ₹{bd.total.toLocaleString()}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })()}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default HotelSelectionCard;
